import { useState, useRef, useCallback } from 'react';
import { useWindowScroll } from './useWindowScroll';

enum ScrollDirection {
    Up = 'up',
    Down = 'down',
    Idle = 'idle',
}

type UseScrollDirectionOptions = {
    /** Минимальное расстояние для определения направления */
    threshold?: number;
};

export const useScrollDirection = (options: UseScrollDirectionOptions = {}) => {
    const { threshold = 10 } = options;

    const [scrollDirection, setScrollDirection] = useState<ScrollDirection>(ScrollDirection.Idle);
    const lastScrollY = useRef(typeof window !== 'undefined' ? window.scrollY : 0);
    const lastDocumentHeight = useRef(typeof window !== 'undefined' ? document.documentElement.scrollHeight : 0);
    const isInitialized = useRef(false);
    const ignoreScrollUntil = useRef(0);

    const updateScrollDirection = useCallback(() => {
        const scrollY = window.scrollY;
        const documentHeight = document.documentElement.scrollHeight;
        const now = Date.now();

        // Пропускаем первый вызов для стабилизации
        if (!isInitialized.current) {
            lastScrollY.current = scrollY;
            lastDocumentHeight.current = documentHeight;
            isInitialized.current = true;

            return;
        }

        // Проверяем, изменилась ли высота документа значительно
        const heightDifference = Math.abs(documentHeight - lastDocumentHeight.current);
        if (heightDifference > 100) {
            // Высота документа изменилась значительно (загрузка контента)
            // Игнорируем скролл в течение 200ms после изменения высоты
            ignoreScrollUntil.current = now + 200;
            lastDocumentHeight.current = documentHeight;
            lastScrollY.current = scrollY;

            return;
        }

        // Игнорируем скролл если еще не прошло достаточно времени после изменения высоты
        if (now < ignoreScrollUntil.current) {
            lastScrollY.current = scrollY;

            return;
        }

        const difference = scrollY - lastScrollY.current;

        // Игнорируем небольшие изменения
        if (Math.abs(difference) < threshold) {
            return;
        }

        const direction: ScrollDirection = difference >= 0 ? ScrollDirection.Down : ScrollDirection.Up;

        // Обновляем состояние только если направление действительно изменилось
        if (direction !== scrollDirection) {
            setScrollDirection(direction);
        }

        lastScrollY.current = scrollY;
        lastDocumentHeight.current = documentHeight;
    }, [threshold, scrollDirection]);

    useWindowScroll(updateScrollDirection, 0, { passive: true });

    return {
        isScrollingUp: scrollDirection === ScrollDirection.Up,
        isScrollingDown: scrollDirection === ScrollDirection.Down,
        isIdle: scrollDirection === ScrollDirection.Idle,
    };
};
